import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Pill, Search, ShieldCheck, Database, AlertTriangle } from "lucide-react";

const STATE_COLORS = {
  covered: "bg-emerald-100 text-emerald-800",
  resolved: "bg-emerald-100 text-emerald-800",
  not_present: "bg-gray-200 text-gray-700",
  not_applicable: "bg-slate-100 text-slate-600",
  not_covered: "bg-rose-100 text-rose-800",
  unknown: "bg-amber-100 text-amber-800",
  unresolved: "bg-amber-100 text-amber-800",
  verification_required: "bg-amber-100 text-amber-900",
  clarification_required: "bg-indigo-100 text-indigo-800",
};

const Pillbox = ({ s }) => (
  <span data-testid="pd-state" className={`px-2 py-0.5 rounded text-xs font-semibold ${STATE_COLORS[s] || "bg-gray-100 text-gray-600"}`}>{s}</span>
);
const bool = (v) => (v === true ? "Yes" : v === false ? "No" : "—");
const amt = (leg) => {
  if (!leg) return "—";
  if (!leg.amount_present) return leg.cost_type ? leg.cost_type : "n/a";
  return `${leg.cost_type === "coinsurance" ? `${leg.amount}%` : `$${leg.amount}`}`;
};

export default function PartDIntel() {
  const [active, setActive] = useState(null);
  const [org, setOrg] = useState("");
  const [plans, setPlans] = useState([]);
  const [q, setQ] = useState({ plan_year: 2026, contract_id: "", plan_id: "", segment_id: "000" });
  const [form, setForm] = useState(null);
  const [drug, setDrug] = useState("");
  const [drugRes, setDrugRes] = useState(null);
  const [rxcui, setRxcui] = useState("");
  const [cov, setCov] = useState(null);
  const [restr, setRestr] = useState(null);
  const [ded, setDed] = useState(null);
  const [cost, setCost] = useState({ tier: "1", coverage_level: "1", days_supply: "30" });
  const [costRes, setCostRes] = useState(null);
  const [pharm, setPharm] = useState(null);
  const [idx, setIdx] = useState(null);
  const [npi, setNpi] = useState("");
  const [net, setNet] = useState(null);
  const [zip, setZip] = useState("");
  const [zipRes, setZipRes] = useState(null);
  const [err, setErr] = useState("");

  const loadActive = async () => {
    const r = await api.get(`/admin/part-d-intel/active`, { params: { plan_year: q.plan_year } });
    setActive(r.data);
  };
  const loadPharm = async () => {
    const r = await api.get(`/admin/part-d-intel/pharmacy-analysis`, { params: { plan_year: q.plan_year } });
    setPharm(r.data);
  };
  const loadIdx = async () => {
    const r = await api.get(`/admin/part-d-intel/pharmacy-index/status`, { params: { plan_year: q.plan_year } });
    setIdx(r.data);
  };
  useEffect(() => { loadActive(); loadPharm(); loadIdx(); }, []); // eslint-disable-line

  const buildIdx = async () => {
    await api.post(`/admin/part-d-intel/pharmacy-index/build`, null, { params: { year: q.plan_year, state: "FL" } });
    setTimeout(loadIdx, 2500);
  };
  const runNet = async () => {
    const r = await api.get(`/admin/part-d-intel/pharmacy-network`, { params: { ...q, npi } });
    setNet(r.data);
  };
  const runZip = async () => {
    const r = await api.get(`/admin/part-d-intel/pharmacy-network/by-zip`, { params: { ...q, zip_code: zip, limit: 25 } });
    setZipRes(r.data);
  };

  const triggerImport = async () => {
    await api.post(`/admin/part-d-intel/imports`, null, { params: { year: q.plan_year, state: "FL" } });
    setErr(""); setTimeout(loadActive, 1500);
  };
  const search = async () => {
    const r = await api.get(`/admin/part-d-intel/plans`, { params: { plan_year: q.plan_year, org, limit: 25 } });
    setPlans(r.data || []);
  };
  const resolveForm = async (over) => {
    setErr(""); setCov(null); setRestr(null); setDed(null); setCostRes(null);
    const params = { ...q, ...(over || {}) };
    try {
      const r = await api.get(`/admin/part-d-intel/formulary`, { params });
      setForm(r.data); setQ((s) => ({ ...s, ...params }));
    } catch (e) { setErr(e?.response?.data?.detail || "lookup failed"); }
  };
  const resolveDrug = async () => {
    const r = await api.get(`/admin/part-d-intel/resolve-drug`, { params: { name: drug } });
    setDrugRes(r.data);
    if (r.data?.state === "resolved" && r.data?.rxcui) setRxcui(r.data.rxcui);
  };
  const runCoverage = async () => {
    const [c, rr] = await Promise.all([
      api.get(`/admin/part-d-intel/coverage`, { params: { ...q, rxcui } }),
      api.get(`/admin/part-d-intel/restrictions`, { params: { ...q, rxcui } }),
    ]);
    setCov(c.data); setRestr(rr.data);
  };
  const runDeductible = async () => {
    const r = await api.get(`/admin/part-d-intel/deductible`, { params: q });
    setDed(r.data);
  };
  const runCost = async () => {
    const r = await api.get(`/admin/part-d-intel/cost-structure`, { params: { ...q, ...cost } });
    setCostRes(r.data);
  };
  const runPharmAnalysis = async () => {
    await api.post(`/admin/part-d-intel/pharmacy-analysis`, null, { params: { year: q.plan_year, state: "FL" } });
    setTimeout(loadPharm, 2000);
  };

  const c = active?.counts || {};
  const x = active?.cross_reference || {};

  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="part-d-intel-page">
      <PageHeader title="Part D Formulary Intelligence QA" icon={Pill}
        subtitle="Deterministic CMS SPUF drug coverage / tier / PA·ST·QL / deductible / cost-sharing structure. No LLM. No cost calculation. Not consumer plan shopping." />

      {/* Active dataset + import */}
      <div className="mt-6 rounded-xl border-2 border-[#E5E5E0] bg-white p-4" data-testid="pd-active">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm font-bold text-[#0B192C] flex items-center gap-2"><Database className="w-4 h-4" />Active FL dataset (PY{q.plan_year})</div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="pd-refresh" onClick={loadActive}>Refresh</Button>
            <Button data-testid="pd-import" onClick={triggerImport}>Run / re-run FL import</Button>
          </div>
        </div>
        {active?.status === "no_active_dataset" ? (
          <p className="mt-2 text-sm text-amber-700" data-testid="pd-no-dataset">No active dataset. Click “Run / re-run FL import”.</p>
        ) : (
          <div className="mt-3 grid sm:grid-cols-4 gap-3 text-sm" data-testid="pd-counts">
            <div><span className="text-[#6B7280]">Plan-segments:</span> <b>{c.fl_plan_segments}</b></div>
            <div><span className="text-[#6B7280]">Formularies:</span> <b>{c.formulary_ingested}/{c.distinct_formularies}</b></div>
            <div><span className="text-[#6B7280]">Drug rows:</span> <b>{c.distinct_drug_rows}</b></div>
            <div><span className="text-[#6B7280]">Bene-cost rows:</span> <b>{c.beneficiary_cost_rows}</b></div>
            <div><span className="text-[#6B7280]">Ambiguous versions:</span> <b>{c.ambiguous_formulary_versions}</b></div>
            <div><span className="text-[#6B7280]">Matched in MA plan-intel:</span> <b>{x.matched_in_ma_plan_intel}</b></div>
            <div><span className="text-[#6B7280]">Not in MA plan-intel:</span> <b>{x.not_in_ma_plan_intel}</b></div>
            <div><span className="text-[#6B7280]">Vintage:</span> <b>{active?.source?.publication_vintage}</b></div>
          </div>
        )}
      </div>

      {/* Find + resolve */}
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-4">
          <div className="text-xs font-semibold text-[#6B7280] flex items-center gap-2"><Search className="w-4 h-4" />Find FL Part D plans</div>
          <div className="flex gap-2 mt-2">
            <Input data-testid="pd-org-input" value={org} onChange={(e) => setOrg(e.target.value)}
              placeholder="e.g. Devoted, CarePlus, Humana" onKeyDown={(e) => e.key === "Enter" && search()} />
            <Button data-testid="pd-org-search" onClick={search}>Search</Button>
          </div>
          <div className="mt-3 max-h-56 overflow-auto divide-y" data-testid="pd-search-results">
            {plans.map((p) => (
              <button key={p.plan_key} data-testid="pd-search-row"
                onClick={() => resolveForm({ contract_id: p.contract_id, plan_id: p.plan_id, segment_id: p.segment_id })}
                className="w-full text-left py-2 hover:bg-[#F8F8F5] px-1">
                <div className="text-sm font-semibold text-[#0B192C]">{p.plan_name || "—"}</div>
                <div className="text-xs text-[#6B7280]">{p.plan_key} · {p.contract_name} · formulary {p.formulary_id} v{p.formulary_version || "?"}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-4">
          <div className="text-xs font-semibold text-[#6B7280]">Resolve by identity (year · contract · PBP · segment)</div>
          <div className="grid grid-cols-4 gap-2 mt-2">
            <Input data-testid="pd-year" value={q.plan_year} onChange={(e) => setQ({ ...q, plan_year: e.target.value })} placeholder="Year" />
            <Input data-testid="pd-contract" value={q.contract_id} onChange={(e) => setQ({ ...q, contract_id: e.target.value })} placeholder="H1290" />
            <Input data-testid="pd-pbp" value={q.plan_id} onChange={(e) => setQ({ ...q, plan_id: e.target.value })} placeholder="013" />
            <Input data-testid="pd-segment" value={q.segment_id} onChange={(e) => setQ({ ...q, segment_id: e.target.value })} placeholder="000" />
          </div>
          <Button data-testid="pd-resolve" className="mt-3 w-full" onClick={() => resolveForm()}>Resolve formulary</Button>
          {err && <p className="mt-2 text-sm text-red-600" data-testid="pd-error">{err}</p>}
        </div>
      </div>

      {form && (
        <div className="mt-6 space-y-4" data-testid="pd-detail">
          <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="font-display text-xl text-[#0B192C]" data-testid="pd-plan-name">{form.plan_name || form.plan_key}</h3>
                <p className="text-sm text-[#6B7280]">{form.contract_name}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Badge variant="secondary">{form.plan_key}</Badge>
                <Badge variant="secondary">Formulary {form.formulary_id} · v{form.formulary_version || "?"}</Badge>
                {form.formulary_version_ambiguous && <Badge className="bg-amber-200 text-amber-900">version ambiguous</Badge>}
              </div>
            </div>
            <div className="mt-2 text-sm" data-testid="pd-form-deductible">
              <span className="text-[#6B7280]">Part D deductible:</span>{" "}
              <b>{form.deductible?.amount_present ? `$${form.deductible.amount}` : "Not applicable"}</b>
            </div>
          </div>

          {/* Drug resolution + coverage */}
          <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-4 space-y-3">
            <div className="text-sm font-bold text-[#0B192C]">Drug coverage (deterministic)</div>
            <div className="flex flex-wrap gap-2 items-center">
              <Input data-testid="pd-drug-input" className="max-w-xs" value={drug} onChange={(e) => setDrug(e.target.value)} placeholder="Drug name e.g. Eliquis" />
              <Button variant="outline" data-testid="pd-resolve-drug" onClick={resolveDrug}>Resolve RXCUI</Button>
              <Input data-testid="pd-rxcui-input" className="max-w-[160px]" value={rxcui} onChange={(e) => setRxcui(e.target.value)} placeholder="RXCUI" />
              <Button data-testid="pd-check-coverage" onClick={runCoverage} disabled={!rxcui}>Check coverage</Button>
            </div>
            {drugRes && (
              <div className="text-sm" data-testid="pd-drug-result">
                <Pillbox s={drugRes.state} />{" "}
                {drugRes.state === "resolved"
                  ? <span className="ml-1">RXCUI <b>{drugRes.rxcui}</b> — {drugRes.drug_name} ({drugRes.tty})</span>
                  : <span className="ml-1 text-[#6B7280]">{drugRes.reason} {Array.isArray(drugRes.candidates) && `(${drugRes.candidates.length} candidates — clarification needed)`}</span>}
                {Array.isArray(drugRes.candidates) && drugRes.candidates.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1" data-testid="pd-drug-candidates">
                    {drugRes.candidates.slice(0, 8).map((cd) => (
                      <button key={cd.rxcui} className="text-xs underline text-[#0B60B0]" onClick={() => setRxcui(cd.rxcui)}>{cd.rxcui}{cd.name ? ` · ${cd.name}` : ""}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {cov && (
              <div className="text-sm border-t pt-3" data-testid="pd-coverage-result">
                <div className="flex items-center gap-2"><Pillbox s={cov.state} />
                  <span className="text-[#6B7280]">{cov.reason || ""}</span></div>
                {cov.state === "covered" && (
                  <div className="mt-1 grid sm:grid-cols-2 gap-1">
                    <div>Tier: <b>{cov.tier_level_value}</b></div>
                    <div>Proxy NDC: <b>{cov.proxy_ndc}</b> <span className="text-[11px] text-amber-700">(proxy, not dispensed)</span></div>
                    {restr && <>
                      <div>Prior authorization: <b>{bool(restr.prior_authorization)}</b></div>
                      <div>Step therapy: <b>{bool(restr.step_therapy)}</b></div>
                      <div>Quantity limit: <b>{bool(restr.quantity_limit)}</b> {restr.quantity_limit ? `(${restr.quantity_limit_amount}/${restr.quantity_limit_days}d)` : ""}</div>
                    </>}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Deductible + cost structure */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-4">
              <div className="text-sm font-bold text-[#0B192C]">Deductible + per-tier applicability</div>
              <Button variant="outline" className="mt-2" data-testid="pd-deductible-btn" onClick={runDeductible}>Load deductible</Button>
              {ded && (
                <div className="mt-2 text-sm" data-testid="pd-deductible-result">
                  <Pillbox s={ded.state} /> <b className="ml-1">{ded.deductible_present ? `$${ded.deductible_amount}` : "Not applicable"}</b>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(ded.per_tier_deductible_applies || []).map((t) => (
                      <span key={t.tier} className="text-xs px-2 py-0.5 rounded bg-[#F8F8F5] border">T{t.tier}: {bool(t.deductible_applies)}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-4">
              <div className="text-sm font-bold text-[#0B192C]">Cost-sharing structure (no calculation)</div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <Input data-testid="pd-cost-tier" value={cost.tier} onChange={(e) => setCost({ ...cost, tier: e.target.value })} placeholder="Tier" />
                <Input data-testid="pd-cost-cl" value={cost.coverage_level} onChange={(e) => setCost({ ...cost, coverage_level: e.target.value })} placeholder="Cov lvl" />
                <Input data-testid="pd-cost-days" value={cost.days_supply} onChange={(e) => setCost({ ...cost, days_supply: e.target.value })} placeholder="Days" />
              </div>
              <Button variant="outline" className="mt-2" data-testid="pd-cost-btn" onClick={runCost}>Load structure</Button>
              {costRes && (
                <div className="mt-2 text-sm" data-testid="pd-cost-result">
                  <Pillbox s={costRes.state} />
                  {costRes.state === "resolved" && (
                    <div className="mt-1 grid grid-cols-2 gap-1">
                      <div>Pref retail: <b>{amt(costRes.preferred_retail)}</b></div>
                      <div>Std retail: <b>{amt(costRes.standard_retail)}</b></div>
                      <div>Pref mail: <b>{amt(costRes.preferred_mail)}</b></div>
                      <div>Std mail: <b>{amt(costRes.standard_mail)}</b></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Pharmacy network (scoped / lazy on-disk index) */}
          <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-4 space-y-3" data-testid="pd-pharmacy-panel">
            <div className="text-sm font-bold text-[#0B192C]">Pharmacy network (scoped resolver)</div>
            <div className="flex flex-wrap gap-2 items-center">
              <Input data-testid="pd-npi-input" className="max-w-[200px]" value={npi} onChange={(e) => setNpi(e.target.value)} placeholder="Pharmacy NPI" />
              <Button data-testid="pd-net-btn" onClick={runNet} disabled={!npi}>Check network</Button>
              <Input data-testid="pd-zip-input" className="max-w-[140px]" value={zip} onChange={(e) => setZip(e.target.value)} placeholder="ZIP" />
              <Button variant="outline" data-testid="pd-zip-btn" onClick={runZip} disabled={!zip}>ZIP lookup</Button>
            </div>
            {net && (
              <div className="text-sm border-t pt-2" data-testid="pd-net-result">
                <div className="flex items-center gap-2"><Pillbox s={net.state} />
                  <span className="text-[#6B7280]">{net.reason || ""}</span>
                  {net.cache && <span className="text-[11px] px-2 py-0.5 rounded bg-[#F8F8F5] border">cache {net.cache}</span>}</div>
                {net.in_network && (
                  <div className="mt-1 grid sm:grid-cols-3 gap-1">
                    <div>In-network: <b>Yes</b></div>
                    <div>Preferred retail: <b>{bool(net.preferred_retail)}</b></div>
                    <div>Preferred mail: <b>{bool(net.preferred_mail)}</b></div>
                    <div>Retail: <b>{bool(net.retail)}</b></div>
                    <div>Mail: <b>{bool(net.mail)}</b></div>
                    <div>In-area: <b>{bool(net.in_area)}</b></div>
                    <div className="sm:col-span-3 text-[11px] text-amber-700">{net.preferred_note}</div>
                  </div>
                )}
                {net.identity && <div className="mt-1 text-xs text-[#6B7280]">Identity: {net.identity.nppes_name || "—"} · ZIP {net.identity.zip}</div>}
              </div>
            )}
            {zipRes && (
              <div className="text-sm border-t pt-2" data-testid="pd-zip-result">
                <Pillbox s={zipRes.state} /> <b className="ml-1">{zipRes.count}</b> in-network pharmacies in {zipRes.zip}
                <div className="mt-1 max-h-40 overflow-auto text-xs divide-y">
                  {(zipRes.pharmacies || []).map((p) => (
                    <div key={p.pharmacy_number} className="py-1">{p.npi} · retail {bool(p.retail)} · mail {bool(p.mail)} · pref-retail {bool(p.preferred_retail)}</div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Provenance */}
          {form.provenance && (
            <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-4" data-testid="pd-provenance">
              <div className="text-sm font-bold text-[#0B192C] flex items-center gap-2"><ShieldCheck className="w-4 h-4" />CMS provenance</div>
              <div className="mt-2 grid sm:grid-cols-2 gap-2 text-xs text-[#374151]">
                <div>Dataset: <b>{form.provenance.dataset}</b></div>
                <div>Table: <b>{form.provenance.source_table}</b></div>
                <div>Vintage: <b>{form.provenance.publication_vintage}</b></div>
                <div>Parser / norm: <b>{form.provenance.parser_version} / {form.provenance.normalization_version}</b></div>
                <div className="sm:col-span-2 break-all">Source: {form.provenance.source_file}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Phase 5 checkpoint */}
      <div className="mt-8 rounded-xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="pd-pharmacy-checkpoint">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm font-bold text-amber-900 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Pharmacy-network volume checkpoint (read-only)</div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="pd-pharm-refresh" onClick={loadPharm}>Refresh</Button>
            <Button data-testid="pd-pharm-analyze" onClick={runPharmAnalysis}>Run analysis</Button>
          </div>
        </div>
        <p className="text-xs text-amber-800 mt-1">Measures FL pharmacy-network volume without persisting any rows. Persistence is a separate, authorized step.</p>
        <div className="mt-3 flex items-center justify-between flex-wrap gap-2 border-t border-amber-200 pt-3" data-testid="pd-index-row">
          <div className="text-sm font-semibold text-amber-900">Scoped on-disk index (P1.1)</div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="pd-index-status" onClick={loadIdx}>Index status</Button>
            <Button data-testid="pd-index-build" onClick={buildIdx}>Build / rebuild index</Button>
          </div>
        </div>
        {idx && (
          <div className="text-xs text-amber-900 mt-1" data-testid="pd-index-info">
            {idx.status === "not_built" ? "Index not built." : (
              <>Status <b>{idx.status}</b> · rows <b>{Number(idx.on_disk?.rows || 0).toLocaleString()}</b> · pharmacies <b>{Number(idx.on_disk?.distinct_pharmacies || 0).toLocaleString()}</b> · {(idx.index_bytes ? (idx.index_bytes / 1e9).toFixed(2) + " GB on disk" : "")} · matches active dataset: <b>{String(idx.matches_active_dataset)}</b></>
            )}
          </div>
        )}
        {pharm?.status === "not_run" ? (
          <p className="mt-2 text-sm text-amber-800" data-testid="pd-pharm-notrun">Not run yet.</p>
        ) : pharm ? (
          <div className="mt-3 grid sm:grid-cols-4 gap-3 text-sm" data-testid="pd-pharm-result">
            <div><span className="text-[#6B7280]">FL network rows:</span> <b>{pharm.total_fl_pharmacy_network_rows?.toLocaleString?.()}</b></div>
            <div><span className="text-[#6B7280]">Distinct pharmacies:</span> <b>{pharm.distinct_pharmacy_numbers?.toLocaleString?.()}</b></div>
            <div><span className="text-[#6B7280]">Rows/plan (avg):</span> <b>{pharm.rows_per_plan?.avg}</b></div>
            <div><span className="text-[#6B7280]">Norm. est. bytes:</span> <b>{pharm.estimated_mongo_bytes?.normalized_total?.toLocaleString?.()}</b></div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
